import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Card,
  Chip,
  DataTable,
  DefinitionList,
  Donut,
  Kpi,
  Legend,
  PageHead,
  Pager,
  RankedBars,
  Skeleton,
  StateBlock,
  TreeGrid,
  TrendChart,
  seriesColor,
  type Column,
  type TreeGridColumn,
} from '@w7/ui'
import { useCommission, useSession } from '@/app/queries'
import { AccountLink, Amount, AsOf, Money, exportCsv } from '@/app/bits'
import { ExportCsvButton } from '@/app/ExportCsvButton'
import { round2 } from '@/lib/book'
import { EMPTY, count, dayLabel, ll, lll, money, moneyCompact, pct, percent, sharePoints } from '@/lib/format'
import type { CommissionRowView } from '@/api/map/commission'
import { ROLE_LABEL, type GameBucket, type Sport } from '@/data/types'
import {
  buildCommissionTree,
  childIndex,
  filterCommissionRows,
  treeTotals,
  type CommissionNode,
} from './commissionTree'
import {
  FilteredEmpty,
  ReportFilters,
  ScopeBanner,
  ScopeUnavailable,
  scopeSuffix,
  scopedCsvName,
  useReportFilters,
  type Dimension,
} from './ReportFilters'

/**
 * Commission ledger — settlement postings down the chain.
 *
 * Distinct from Eligibility (week qualify list) and Fancy & Bookmaker (product
 * KPI cards). Live path: GET /agents/{id}/commission (slice a6). Until a6 the
 * API returns 503 Unavailable — this page says so rather than inventing numbers
 * from share_bp or member-pnl. Demo/fixture still uses event-level getPnl rows.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

/**
 * Scope, sport, game and member go into the QUERY on the fixture path; live,
 * only scope is in the commission contract and the rest narrow client-side.
 * Tier and direction are absent for the same reason as My P&L — every row is
 * member-grain, and a settled row has no direction.
 */
const DIMENSIONS: Dimension[] = ['range', 'scope', 'sport', 'game', 'member', 'q']

export function CommissionPage() {
  const filters = useReportFilters(DIMENSIONS)
  const { values, scopeAccount } = filters
  const range = values.range
  const { data: session } = useSession()

  const requestedPage = values.page

  const dates = useMemo(() => ({ from: range.from, to: range.to }), [range.from, range.to])

  const { data, isLoading, dataUpdatedAt } = useCommission({
    range: dates,
    ...(values.scope ? { rootId: values.scope } : {}),
    ...(values.sports.length ? { sports: values.sports } : {}),
    ...(values.games.length ? { games: values.games } : {}),
    ...(values.members.length ? { memberIds: values.members } : {}),
  })

  const unavailable = data?.unavailable
  const eventRows = data?.eventRows
  const ledgerRows = data?.rows ?? []
  const totals = data?.totals
  const asOf = data?.asOf ?? (dataUpdatedAt || undefined)

  const bearingEvents = useMemo(() => eventRows ?? null, [eventRows])

  /* ------------------------------------------------------------- the tree -- */

  /**
   * The rows the free-text box leaves standing.
   *
   * This box has always said "Filter events and markets" and, until now, filtered
   * nothing — the chip appeared, `isFiltered` flipped the empty state, and the
   * rows were untouched. The tree and the CSV both read THIS array, so the file
   * an operator exports is the set they were looking at.
   *
   * Everything above the table — the KPI tiles, the daily chart, the sport and
   * game breakouts — deliberately keeps reading the unfiltered `bearingEvents`,
   * because those describe the period, not the table.
   */
  const visibleEventRows = useMemo(
    () => filterCommissionRows(bearingEvents ?? [], values.q),
    [bearingEvents, values.q],
  )

  /** null while there is no event data at all — loading, or a live path. */
  const tree = useMemo(
    () => (bearingEvents ? buildCommissionTree(visibleEventRows) : null),
    [bearingEvents, visibleEventRows],
  )
  const treeChildren = useMemo(() => childIndex(tree ?? []), [tree])
  /** The tree's own grand total — the FILTERED set, which is what it displays. */
  const bearingTotals = useMemo(() => (tree ? treeTotals(tree) : null), [tree])

  /**
   * Commission-bearing bets over the whole period, filter or no filter.
   *
   * The KPI beside it counts every settled bet in the range, so its companion
   * has to be counted over the same set — narrowing this one with the table's
   * text box would print a percentage of two different denominators.
   */
  const bearingBetCount = useMemo(
    () => bearingEvents?.reduce((sum, row) => sum + row.betCount, 0) ?? null,
    [bearingEvents],
  )

  /**
   * Expanded keys, held here because `TreeGrid` is fully controlled.
   *
   * Keys are derived from event / bucket / market names, so they survive a
   * refetch and a page change — open a branch, page away and back, and it is
   * still open. Keys for branches that a filter has since removed simply never
   * match anything, which is why nothing prunes this.
   */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())

  const series = useMemo(() => {
    const daily = data?.daily
    if (!daily) return null
    return {
      categories: daily.map((d) => dayLabel(d.date)),
      commission: daily.map((d) => d.commission),
      net: daily.map((d) => d.netPnl),
    }
  }, [data])

  const bySport = useMemo(() => {
    if (!bearingEvents || data?.sportGameUnavailable) return null
    const map = new Map<Sport, number>()
    for (const row of bearingEvents) map.set(row.sport, round2((map.get(row.sport) ?? 0) + row.commission))
    return [...map.entries()]
      .map(([sport, commission]) => ({ sport, commission }))
      .sort((a, b) => b.commission - a.commission)
  }, [bearingEvents, data?.sportGameUnavailable])

  const byGame = useMemo(() => {
    if (!bearingEvents || data?.sportGameUnavailable) return null
    const map = new Map<GameBucket, number>()
    for (const row of bearingEvents) map.set(row.game, round2((map.get(row.game) ?? 0) + row.commission))
    return [...map.entries()]
      .map(([game, commission]) => ({ game, commission }))
      .sort((a, b) => b.commission - a.commission)
  }, [bearingEvents, data?.sportGameUnavailable])

  /**
   * The tree's columns.
   *
   * Five, not the flat table's seven: event, market and member were three
   * separate columns repeating the same strings down hundreds of rows, and they
   * are now the tree column's four levels. Every money column shows the node's
   * OWN figure on a leaf and the roll-up of everything beneath it on a branch —
   * the `help` text says so on each, because a column whose grain changes with
   * depth and does not admit it is how a branch total gets read as a bet.
   */
  const treeColumns = useMemo<TreeGridColumn<CommissionNode>[]>(
    () => [
      {
        id: 'name',
        header: 'Event, bucket, market, bet',
        cell: ({ row: node }) =>
          node.row ? (
            /* The leaf is the settled row: when it happened and who it was.
               Both, because "ravi_07" three times under one market is only
               distinguishable by the clock. */
            <span className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
              <span className="w7-dim">{lll(node.row.settledAt)}</span>
              <span className="w7-dim">·</span>
              <AccountLink id={node.row.memberId} username={node.label} />
            </span>
          ) : (
            /* max-width on a table cell is advisory and the kit sets nowrap, so
               a long fixture name would widen the column and push the whole
               table into horizontal scroll. Truncate here or not at all. */
            <span
              className="w7-truncate"
              style={{ display: 'block', maxWidth: node.kind === 'event' ? 340 : 280 }}
              title={node.kind === 'event' ? `${node.label} · ${ll(node.at)}` : node.label}
            >
              {node.label}
              {/* The day, on event roots only. The same race card and the same
                  league fixture come round every few days, so the name alone
                  cannot tell Tuesday's from Friday's. */}
              {node.kind === 'event' ? (
                <span className="w7-dim"> · {ll(node.at)}</span>
              ) : null}
            </span>
          ),
      },
      {
        id: 'bets',
        header: 'Bets',
        numeric: true,
        help: 'Settled bets beneath this row — the row’s own count on a bet, the sum of the branch above it.',
        cell: ({ row: node }) => <span className="w7-dim">{count(node.betCount)}</span>,
        hideBelow: 720,
      },
      {
        id: 'member-pnl',
        header: 'Member P&L',
        numeric: true,
        help: 'The members’ own result before commission, rolled up. Everything here is a win for the member — that is why it carried commission at all.',
        cell: ({ row: node }) => <Money value={node.memberPnl} signed invert />,
        hideBelow: 900,
      },
      {
        id: 'commission',
        header: 'Commission',
        numeric: true,
        help: 'Charged on the member’s net winnings at the rate the market carries, and already netted out of the net beside it. A branch is exactly the sum of the rows inside it.',
        cell: ({ row: node }) => <Amount value={node.commission} />,
      },
      {
        id: 'net',
        header: 'Net to book',
        numeric: true,
        help: 'Member P&L after commission, from the book’s side.',
        cell: ({ row: node }) => <Money value={node.netToBook} signed />,
        hideBelow: 720,
      },
    ],
    [],
  )

  const ledgerColumns = useMemo<Column<CommissionRowView>[]>(
    () => [
      {
        id: 'date',
        header: 'Date',
        cell: (row) => <span className="w7-dim">{ll(row.date)}</span>,
        sortValue: (row) => row.date,
        width: 120,
        total: <span className="w7-dim">All ledger rows</span>,
      },
      {
        id: 'agent',
        header: 'Agent',
        cell: (row) => <AccountLink id={row.agentId} username={row.agentName} />,
        sortValue: (row) => row.agentName,
      },
      {
        id: 'direction',
        header: 'Direction',
        cell: (row) => (
          <Chip tone={row.direction === 'earned' ? 'brand' : 'info'} size="sm">
            {row.direction === 'earned' ? 'Earned' : 'Paid'}
          </Chip>
        ),
        sortValue: (row) => row.direction,
        hideBelow: 720,
      },
      {
        id: 'commission',
        header: 'Commission',
        numeric: true,
        help: 'Ledger posting amount from settlement_postings — never derived from share %.',
        cell: (row) => <Amount value={row.commission} />,
        sortValue: (row) => row.commission,
        total: <Amount value={totals?.commission} />,
      },
      {
        id: 'source',
        header: 'Source',
        cell: () => <span className="w7-dimmer">settlement_postings</span>,
        sortValue: () => 'settlement_postings',
        hideBelow: 1080,
      },
    ],
    [totals?.commission],
  )


  /* The rates card describes whoever's seat this report is being read from —
     under a scope those are THEIR rates, and showing yours beside their
     commission figures would invite exactly the wrong arithmetic. */
  const account = scopeAccount ?? session?.account
  const commissionTotal = unavailable ? null : (totals?.commission ?? null)
  const beforeCommission =
    totals && commissionTotal !== null ? round2(totals.netPnl - commissionTotal) : null
  const shareOfTurnover =
    totals && commissionTotal !== null && totals.turnover > 0
      ? commissionTotal / totals.turnover
      : null

  /**
   * Nothing has resolved yet, so the MODE is not known either.
   *
   * `tableMode` falls through to 'ledger' while `data` is undefined, which on a
   * cold fixture load painted the ledger's title, its analytics-contract
   * subtitle and its five ledger columns for a beat before the tree replaced
   * them. Claiming the wrong table and then swapping is worse than claiming
   * none.
   */
  const settling = isLoading && !data

  const tableMode: 'unavailable' | 'ledger' | 'events' = unavailable
    ? 'unavailable'
    : eventRows
      ? 'events'
      : 'ledger'

  /**
   * Paging counts EVENTS in tree mode and ledger rows in ledger mode.
   *
   * Deliberately two variables rather than one shared `rowsForPager`: the pager
   * says "1–25 of 84", and in tree mode the honest unit for that sentence is the
   * event you can open, not the bets hidden inside it. Slicing bets instead
   * would cut a branch in half across a page boundary.
   */
  const pagedItems: readonly unknown[] =
    tableMode === 'events' ? (tree ?? []) : tableMode === 'ledger' ? ledgerRows : []
  const pageCount = Math.max(1, Math.ceil(pagedItems.length / PAGE_SIZE))
  const page = Math.min(requestedPage, pageCount)
  const sliceFrom = (page - 1) * PAGE_SIZE
  const treePage = (tree ?? []).slice(sliceFrom, sliceFrom + PAGE_SIZE)
  const ledgerPage = ledgerRows.slice(sliceFrom, sliceFrom + PAGE_SIZE)

  /** What Export CSV would actually write — drives its disabled state. */
  const exportableRows = tableMode === 'events' ? visibleEventRows.length : ledgerRows.length

  const onExport = () => {
    if (tableMode === 'events' && bearingEvents) {
      exportCsv(
        scopedCsvName('commission', scopeAccount, range),
        // The FILTERED rows, flat and unpaged: the tree's four levels are a way
        // of reading this set, not a different set, and a spreadsheet groups
        // itself. Flat also keeps Game and Sport, which the table never shows.
        visibleEventRows.map((row) => ({
          Settled: lll(row.settledAt),
          Event: row.eventName,
          Market: row.marketName,
          Game: row.game,
          Sport: row.sport,
          Member: row.memberId,
          'Member P&L': money(row.memberPnl, { symbol: false, signed: true }),
          Commission: money(row.commission, { symbol: false }),
          'Net to book': money(-row.netAmount, { symbol: false, signed: true }),
        })),
      )
      return
    }
    exportCsv(
      scopedCsvName('commission', scopeAccount, range),
      ledgerRows.map((row) => ({
        Date: ll(row.date),
        Agent: row.agentName,
        AgentId: row.agentId,
        Direction: row.direction,
        Commission: money(row.commission, { symbol: false }),
        Source: row.source,
      })),
    )
  }

  const head = (
    <PageHead
      title="Commission ledger"
      // The prefix is always the SIGNED-IN account and the suffix names the seat
      // being read, so the two never say the same name twice.
      subtitle={`${session ? `${session.account.username} · ${ROLE_LABEL[session.account.role]}` : EMPTY} · ${range.label}${scopeSuffix(scopeAccount)} — commission is a ledger figure from settlement postings, charged on a member’s net winnings.`}
    />
  )

  if (filters.scopeInvalid) {
    return (
      <>
        {head}
        <ScopeUnavailable onClear={() => filters.clearDimension('scope')} />
      </>
    )
  }

  return (
    <>
      {head}

      <ReportFilters
        state={filters}
        memberSource="settled"
        textLabel="Filter events and markets"
        textPlaceholder="Event or market"
        omission="Tier and direction are not offered here: every commission-bearing row belongs to one member, and a settled row has no give or take direction."
        actions={
          <>
            <AsOf at={asOf} />
            {data?.martStale ? (
              <span className="w7-dimmer" style={{ fontSize: 12 }}>
                Mart marked stale
              </span>
            ) : null}
            <ExportCsvButton
              onClick={onExport}
              disabled={tableMode === 'unavailable' || exportableRows === 0}
              count={exportableRows}
            />
          </>
        }
      />

      {scopeAccount ? (
        <ScopeBanner account={scopeAccount} onClear={() => filters.clearDimension('scope')} />
      ) : null}

      {unavailable ? (
        <div style={{ marginBottom: 'var(--w7-s5)' }}>
          <StateBlock icon="info" title="Commission is not available yet">
            {unavailable.reason}
            {unavailable.trackedBy ? ` Tracked by ${unavailable.trackedBy}.` : null} The OpenAPI
            returns 503 with this reason until slice a6 — the console will not invent commission from
            share % or member P&L. Book net below still comes from daily marts when present. Your
            rates card is account terms from your upline, not charged amounts.
          </StateBlock>
        </div>
      ) : null}

      <div className="w7-grid w7-grid-4" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Commission charged"
          value={commissionTotal === null ? EMPTY : moneyCompact(commissionTotal)}
          exact={commissionTotal === null ? undefined : money(commissionTotal)}
          accent
          meta={
            unavailable
              ? 'Waiting for settlement_postings (slice a6)'
              : totals
                ? tableMode === 'ledger' && totals.paid > 0
                  ? `Earned ${moneyCompact(totals.earned)} · paid ${moneyCompact(totals.paid)}`
                  : 'Ledger commission in this period'
                : 'Charged on winning bets only'
          }
        />
        <Kpi
          label="Commission on turnover"
          value={shareOfTurnover === null ? EMPTY : percent(shareOfTurnover, 2)}
          meta={
            unavailable
              ? 'Not computed while commission is unavailable'
              : totals && commissionTotal !== null
                ? `${money(commissionTotal, { round: true })} on ${moneyCompact(totals.turnover)} of matched volume`
                : undefined
          }
        />
        <Kpi
          label="Net P&L before commission"
          value={beforeCommission === null ? EMPTY : moneyCompact(beforeCommission)}
          exact={money(beforeCommission)}
          tone={beforeCommission !== null && beforeCommission < 0 ? 'loss' : 'profit'}
          meta={
            unavailable
              ? totals
                ? `Book net ${money(totals.netPnl, { round: true })} (commission not subtracted — unknown)`
                : undefined
              : totals && commissionTotal !== null
                ? `Commission takes it to ${money(totals.netPnl, { round: true })}`
                : 'What the book would have held with no commission charged'
          }
        />
        <Kpi
          label="Settled bets"
          value={totals ? count(totals.bets) : EMPTY}
          meta={
            bearingBetCount !== null && totals && totals.bets > 0
              ? `${count(bearingBetCount)} carried commission · ${percent(bearingBetCount / totals.bets, 1)} of the total`
              : totals
                ? 'From daily mart (bets settled)'
                : undefined
          }
        />
      </div>

      <Card
        title="Commission against net P&L, by day"
        subtitle={
          unavailable
            ? 'Book net from GET /daily. Commission bars stay at zero until ledger postings land — a flat zero is honest; a guessed series would not be.'
            : 'Bars are the commission charged that day; the line is the book’s net for the same day, on its own axis.'
        }
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        {series ? (
          <TrendChart
            label="Daily commission charged with the book’s daily net profit and loss"
            categories={series.categories}
            height={260}
            maxLabels={8}
            format={(v) => moneyCompact(v, false)}
            formatRight={(v) => moneyCompact(v, false)}
            series={[
              {
                id: 'commission',
                label: 'Commission charged',
                values: series.commission,
                kind: 'bar',
                color: 'var(--w7-c3)',
              },
              {
                id: 'net',
                label: 'Net P&L',
                values: series.net,
                kind: 'line',
                color: 'var(--w7-c1)',
                axis: 'right',
              },
            ]}
            emptyMessage="Nothing settled in this period"
          />
        ) : (
          <ChartSkeleton height={260} />
        )}
      </Card>

      <div className="w7-grid w7-grid-3" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Card title="Commission by sport" subtitle="Where the winning bets were">
          {data?.sportGameUnavailable ? (
            <StateBlock icon="commission" title="Unavailable — no market catalogue">
              Sport is not on GET /commission. Needs a market catalogue (same gap as turnover by
              sport).
            </StateBlock>
          ) : bySport ? (
            <RankedBars
              label="Commission charged by sport"
              rows={bySport.map((s) => ({ label: s.sport, value: s.commission }))}
              format={(v) => moneyCompact(v)}
              emptyMessage="No commission was charged in this period"
            />
          ) : (
            <ChartSkeleton height={190} />
          )}
        </Card>

        <Card title="Commission by game" subtitle="The buckets your downline won in">
          {data?.sportGameUnavailable ? (
            <StateBlock icon="commission" title="Unavailable — no product axis">
              Game/product breakouts are not in the analytics contract for commission.
            </StateBlock>
          ) : byGame ? (
            <>
              <Donut
                label="Commission charged by game bucket"
                height={190}
                slices={byGame.map((g, i) => ({
                  label: g.game,
                  value: g.commission,
                  color: seriesColor(i),
                }))}
                format={(v) => money(v, { round: true })}
                centerValue={commissionTotal !== null ? moneyCompact(commissionTotal) : EMPTY}
                centerLabel="commission"
                emptyMessage="No commission was charged in this period"
              />
              <Legend
                items={byGame.map((g, i) => ({
                  label: g.game,
                  color: seriesColor(i),
                  value: moneyCompact(g.commission),
                }))}
              />
            </>
          ) : (
            <ChartSkeleton height={190} />
          )}
        </Card>

        <Card
          title={scopeAccount ? `${scopeAccount.username}’s rates` : 'Your rates'}
          subtitle={`Set on ${scopeAccount ? 'their' : 'your'} account by ${scopeAccount ? 'their' : 'your'} upline. These are what applies from here on — charged amounts above come from the ledger when available.`}
        >
          {account ? (
            <DefinitionList
              columns={1}
              items={[
                {
                  label: (
                    <RateLabel
                      name="Fancy commission"
                      meaning="Taken from a member’s net winnings on fancy markets."
                    />
                  ),
                  value: <span className="w7-mono">{pct(account.commission.fancy)}</span>,
                },
                {
                  label: (
                    <RateLabel
                      name="Bookmaker commission"
                      meaning="Taken from a member’s net winnings on bookmaker markets."
                    />
                  ),
                  value: <span className="w7-mono">{pct(account.commission.bookmaker)}</span>,
                },
                {
                  label: (
                    <RateLabel
                      name="Match odds commission"
                      meaning="Taken from a member’s net winnings on match odds markets."
                    />
                  ),
                  value: <span className="w7-mono">{pct(account.commission.matchOdds)}</span>,
                },
                {
                  label: (
                    <RateLabel
                      name="My share"
                      meaning={`${scopeAccount ? 'Their' : 'Your'} slice of every sportsbook amount that settles beneath ${scopeAccount ? 'them' : 'you'}.`}
                    />
                  ),
                  value: <span className="w7-mono">{sharePoints(account.share.sportsbook)}</span>,
                },
                {
                  label: (
                    <RateLabel
                      name="Casino My share"
                      meaning={`${scopeAccount ? 'Their' : 'Your'} slice of every casino amount that settles beneath ${scopeAccount ? 'them' : 'you'}.`}
                    />
                  ),
                  value: <span className="w7-mono">{sharePoints(account.share.casino)}</span>,
                },
              ]}
            />
          ) : (
            <RatesSkeleton />
          )}
        </Card>
      </div>

      <Card
        title={
          settling
            ? 'Where the commission came from'
            : tableMode === 'ledger'
              ? 'Commission ledger rows'
              : 'Where the commission came from'
        }
        subtitle={
          settling
            ? undefined
            : tableMode === 'unavailable'
            ? 'Ledger rows appear here once GET /commission returns 200 with settlement_postings.'
            : tableMode === 'ledger'
              ? 'One row per CommissionRow from the analytics contract — date, agent, direction, amount. No event/member fields on this endpoint.'
              : 'Only the settled rows that carried commission — a member has to win for any to be charged, so this is not the whole book. Biggest event first; open one for its game buckets, then its markets, then the bets themselves, every branch exactly the sum of what is inside it (fixture demo).'
        }
        flush
        footer={
          settling || tableMode === 'unavailable' ? undefined : (
            <>
              {/* The totals TreeGrid cannot draw. It has no totals row and its
                  node footer is one merged cell per branch, so the grand total
                  has to live out here — and it describes the filtered set the
                  tree is showing, not the whole range, which is what the tiles
                  above are for. */}
              {/* Nothing to total is not "₹0.00 of nothing": under a
                  "no rows matched" block, a zeroed totals strip reads as a
                  figure rather than as an absence. */}
              {tableMode === 'events' && bearingTotals && bearingTotals.betCount > 0 ? (
                <span className="w7-row" style={{ gap: 'var(--w7-s3)' }}>
                  <span className="w7-dim">
                    {/* "All" only once there is more than one page: beside a pager
                        reading "1–25 of 60", a bare "60 events" invites reading it
                        as the page. On a single page the word is just noise. */}
                    {pageCount > 1 ? 'All ' : ''}
                    {count(pagedItems.length)} {pagedItems.length === 1 ? 'event' : 'events'} ·{' '}
                    {/* "commission-bearing", never "settled bets": the tile above
                        says "Settled bets" over EVERY settled bet in the range,
                        and these are only the ones a member won. Two counts, the
                        same words and a factor of three between them is how the
                        page gets read as broken. */}
                    {count(bearingTotals.betCount)} commission-bearing bets
                  </span>
                  <span>
                    <span className="w7-dim">Commission </span>
                    <Amount value={bearingTotals.commission} />
                  </span>
                  <span>
                    <span className="w7-dim">Net to book </span>
                    <Money value={bearingTotals.netToBook} signed />
                  </span>
                </span>
              ) : null}
              <span style={{ marginLeft: 'auto' }}>
                <Pager
                  page={page}
                  pageCount={pageCount}
                  onChange={filters.setPage}
                  total={pagedItems.length}
                  pageSize={PAGE_SIZE}
                />
              </span>
            </>
          )
        }
      >
        {settling ? (
          <div style={{ display: 'grid', gap: 'var(--w7-s3)', padding: 'var(--w7-s5)' }}>
            {Array.from({ length: 12 }, (_, i) => (
              <Skeleton key={i} height={14} />
            ))}
          </div>
        ) : tableMode === 'unavailable' ? (
          <StateBlock icon="commission" title="No ledger commission rows yet">
            {unavailable?.reason ??
              'Commission comes from settlement postings (slice a6). Widen nothing — inventing rows would look like a finished report.'}
          </StateBlock>
        ) : tableMode === 'events' ? (
          <TreeGrid
            label="Commission by event, game bucket, market and settled bet"
            roots={treePage}
            columns={treeColumns}
            rowKey={(node) => node.key}
            hasChildren={(node) => node.children.length > 0}
            /* The whole tree is already in memory — one query produced it — so
               this is a synchronous lookup and there is no onLoadChildren to
               pass. Inline rather than useCallback on purpose: it is a memo
               dependency inside the grid, and a stale identity would freeze
               every open branch on the previous filter's children. */
            childrenOf={(key) => treeChildren.get(key)}
            expanded={expanded}
            onExpandedChange={setExpanded}
            rowLabel={(node) => node.label}
            loading={isLoading}
            skeletonRows={12}
            empty={
              filters.isFiltered ? (
                <FilteredEmpty state={filters} icon="commission" />
              ) : (
                <StateBlock icon="commission" title="No commission was charged in this period">
                  Commission comes off a member’s winnings, so a period in which every settled bet
                  lost carries none.
                </StateBlock>
              )
            }
          />
        ) : (
          <DataTable
            label="Ledger commission rows"
            rows={ledgerPage}
            columns={ledgerColumns}
            rowKey={(row) => row.id}
            loading={isLoading}
            skeletonRows={12}
            showTotals={ledgerRows.length > 0}
            empty={
              <StateBlock icon="commission" title="No commission was posted in this period">
                When settlement_postings land for this range, earned and paid rows appear here.
              </StateBlock>
            }
          />
        )}
      </Card>
    </>
  )
}

function RateLabel({ name, meaning }: { name: string; meaning: string }) {
  return (
    <span style={{ display: 'block', whiteSpace: 'normal' }}>
      {name}
      <span className="w7-dimmer" style={{ display: 'block', fontSize: 12 }}>
        {meaning}
      </span>
    </span>
  )
}

function ChartSkeleton({ height }: { height: number }) {
  return (
    <div style={{ display: 'flex', height }} aria-hidden="true">
      <Skeleton height="100%" radius={10} />
    </div>
  )
}

function RatesSkeleton() {
  return (
    <div className="w7-col" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} height={38} radius={8} />
      ))}
    </div>
  )
}
